import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Textarea,
  useDisclosure,
  Skeleton,
  Center,
  Spinner,
  Checkbox,
} from "@chakra-ui/react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import dayjs from "dayjs";
import { mutateFunction, useMutation } from "../../../libs/apis";
import { CustomToast } from "../../../components/toast/CustomToast";
import exportToCSV from "../../../utils/ExportToCSV";
import BrandButton from "../../../components/brandButton/BrandButton";
import { useNavigate } from "react-router-dom";

export default function AdminPitchRequestsTable({ requests = [], refetch, isLoading, isError = false }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate, isLoading: isMutating } = useMutation(mutateFunction); // Renamed to avoid conflict
  const { addToast } = CustomToast();
  const navigate = useNavigate();

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionType, setActionType] = useState("");
  const [reason, setReason] = useState("");
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);

  const openModal = (request, type) => {
    setSelectedRequest(request);
    setActionType(type);
    setReason("");
    onOpen();
  };

  const handleAction = async (e) => {
    e.preventDefault();
    if (actionType === "declined" && !reason.trim()) {
      addToast({ message: "Please provide a reason for declining." }, "warning");
      return;
    }

    const data = {
      status: actionType === "invited" ? "invited" : "declined",
      note: reason.trim() || null,
    }

    if (actionType == "invited" && selectedRequest?.event_id) {
      data["event_id"] = selectedRequest.event_id
    }

    mutate(
      {
        key: `admin/pitch-requests/${selectedRequest.id}`,
        method: "patch",
        data
      },
      {
        onSuccess(res) {
          addToast(res);
          onClose();
          refetch()
        },
        onError(e) {
          addToast({ message: "Failed to update request." }, "error");
        },
      }
    );
  };

  const handleSelectRow = (id) => {
    setSelectedRequestIds((prev) =>
      prev.includes(id)
        ? prev.filter((prevId) => prevId !== id) // Remove ID
        : [...prev, id] // Add ID
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = requests.map((req) => req.id);
      setSelectedRequestIds(allIds);
    } else {
      setSelectedRequestIds([]);
    }
  };

  const handleExport = () => {
    // 1. Filter the main requests data based on selected IDs
    const selectedRequests = requests.filter((req) =>
      selectedRequestIds.includes(req.id)
    );

    // 2. Define headers for the CSV
    const headers = [
      "Request ID",
      "Founder Name",
      "Founder Email",
      "Company",
      "Pitch Deck URL",
      "Event",
      "Status",
      "Date Requested",
    ];

    // 3. Map the selected data to the CSV row format
    const rows = selectedRequests.map((req) => [
      req.id,
      `${req?.founder?.first_name || ''} ${req.founder?.last_name || '-'}`,
      req?.founder?.email || "N/A", // Added email as it's often useful
      req?.company?.title || "N/A",
      req?.deck_url || "N/A",
      req?.event?.name || "—",
      req.status,
      dayjs(req.createdAt).format("YYYY-MM-DD HH:mm"), // Using a standard date format
    ]);

    // 4. Combine headers and rows
    const csvData = [headers, ...rows];

    // 5. Call the export function
    exportToCSV(csvData, `pitch-requests-export-${dayjs().format('YYYYMMDD')}.csv`);
  };


  // const openFounderModal = (request) => {
  //   setSelectedFounder(request.founder);
  //   setSelectedCompany(request.company);
  //   onFounderModalOpen();
  // };

  const viewProfile = (request) => {
    navigate(`/view-user?userid=${request.founder.id}&usertype=${request.founder.userTypeId}&source=admin`);
  };

  // NEW: Determine the state of the "select all" checkbox
  const isAllSelected = requests.length > 0 && selectedRequestIds.length === requests.length;
  const isIndeterminate = selectedRequestIds.length > 0 && !isAllSelected;
  // Loading skeleton component
  const TableSkeleton = () => (
    <Box className="mt-4 w-full max-w-6xl mx-auto overflow-x-auto">
      <Table variant="simple" className="min-w-full">
        <Thead bg="gray.50">
          <Tr>
            <Th>Founder</Th>
            <Th>Founder Info</Th>
            <Th>Company</Th>
            <Th>Pitch Deck</Th>
            <Th>Event</Th>
            <Th>Status</Th>
            <Th>Date</Th>
            <Th textAlign="center">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {[1, 2, 3, 4, 5].map((row) => (
            <Tr key={row}>
              <Td><Skeleton height="20px" width="120px" /></Td>
              <Td><Skeleton height="20px" width="80px" /></Td>
              <Td><Skeleton height="20px" width="100px" /></Td>
              <Td><Skeleton height="20px" width="90px" /></Td>
              <Td><Skeleton height="20px" width="80px" /></Td>
              <Td><Skeleton height="20px" width="70px" /></Td>
              <Td><Skeleton height="20px" width="100px" /></Td>

              <Td>
                <Flex justify="center" gap={3}>
                  <Skeleton height="32px" width="80px" />
                  <Skeleton height="32px" width="80px" />
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );

  // Show loading skeleton when data is loading
  if (isLoading) {
    return <TableSkeleton />;
  }

  // Show error state if there's an error
  if (isError) {
    return (
      <Center h="200px">
        <Text color="red.500">Error loading pitch requests. Please try again.</Text>
      </Center>
    );
  }

  return (
    <Box className="mt-4 w-full max-w-6xl mx-auto">

      {/* NEW: Export Button Area */}
      <Flex justify="flex-end" mb={4}>
        <BrandButton
          onClick={handleExport}
          isDisabled={selectedRequestIds.length === 0}
          colorScheme="blue"
          text={`Export (${selectedRequestIds.length})`}
        />
      </Flex>

      <Box className="overflow-x-auto">
        <Table variant="simple" className="min-w-full">
          <Thead bg="gray.50">
            <Tr>
              {/* NEW: "Select All" Checkbox Header */}
              <Th paddingX={2} width="1%">
                <Checkbox
                  isChecked={isAllSelected}
                  isIndeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  isDisabled={requests.length === 0}
                />
              </Th>
              <Th>Founder</Th>
              <Th>Founder Info</Th>
              <Th>Company</Th>
              <Th>Pitch Deck</Th>
              <Th>Event</Th>
              <Th>Status</Th>
              <Th>Date</Th>
              <Th textAlign="center">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {requests.length === 0 ? (
              <Tr>
                {/* NEW: Updated colSpan to 8 */}
                <Td colSpan={8}>
                  <Flex justify="center" className="py-8">
                    <Text className="text-gray-500">No pitch requests available.</Text>
                  </Flex>
                </Td>
              </Tr>
            ) : (
              requests.map((req) => (
                <Tr
                  key={req.id}
                  _hover={{ bg: "gray.50" }}
                  // NEW: Visually indicate selection
                  bg={selectedRequestIds.includes(req.id) ? "blue.50" : "transparent"}
                >
                  {/* NEW: Row Checkbox */}
                  <Td paddingX={2}>
                    <Checkbox
                      isChecked={selectedRequestIds.includes(req.id)}
                      onChange={() => handleSelectRow(req.id)}
                    />
                  </Td>
                  <Td>{`${req?.founder?.first_name || ''} ${req.founder?.last_name || "-"}` || "N/A"}</Td>
                  <Td>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={() => viewProfile(req)}
                    >
                      View Profile
                    </Button>
                  </Td>
                  <Td>{req?.company?.title || "N/A"}</Td>
                  <Td>
                    {req?.deck_url ? (
                      <Text
                        as="a"
                        href={req.deck_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="blue.500"
                        textDecoration="underline"
                        cursor="pointer"
                        _hover={{ color: "blue.700" }}
                      >
                        View Deck
                      </Text>
                    ) : (
                      "N/A"
                    )}
                  </Td>
                  <Td>{req?.event?.name || "—"}</Td>
                  <Td>
                    <Text
                      className={`capitalize font-medium ${req.status === "pending"
                        ? "text-yellow-600"
                        : req.status === "shortlisted"
                          ? "text-blue-600"
                          : req.status === "invited"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                    >
                      {req.status}
                    </Text>
                  </Td>
                  <Td>{dayjs(req.createdAt).format("MMM D, YYYY")}</Td>
                  <Td>

                    {req.status == "pending" ? <Flex justify="center" gap={3}>
                      <Button
                        size="sm"
                        colorScheme="green"
                        leftIcon={<AiOutlineCheckCircle />}
                        onClick={() => openModal(req, "invited")}
                        isDisabled={req.status !== "pending"}
                      >
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        leftIcon={<AiOutlineCloseCircle />}
                        onClick={() => openModal(req, "declined")}
                        isDisabled={req.status !== "pending"}
                      >
                        Decline
                      </Button>
                    </Flex> : <Text
                      className={`capitalize font-medium ${req.status === "invited"
                        ? "text-green-600"
                        : req.status === "declined"
                          ? "text-red-600"
                          : req.status === "shortlisted"
                            ? "text-blue-600"
                            : "text-gray-600"
                        }`}
                    >
                      {req.status === "invited"
                        ? "Accepted"
                        : req.status === "declined"
                          ? "Declined"
                          : req.status === "shortlisted"
                            ? "Shortlisted"
                            : req.status}
                    </Text>}

                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Action Modal (No changes here) */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={6}>
            <form onSubmit={handleAction}>
              <Flex direction="column" gap={4}>
                <Text
                  fontSize="18px"
                  fontWeight="600"
                >
                  {actionType === "invited" ? "Accept Pitch Request" : "Decline Pitch Request"}
                </Text>

                <Text fontSize="sm" color="gray.600">
                  {actionType === "invited"
                    ? "You can optionally include a note or reason for accepting."
                    : "Please include a reason for declining this request."}
                </Text>

                <Textarea
                  name="reason"
                  placeholder={
                    actionType === "invited"
                      ? "Optional reason or note..."
                      : "Reason for declining..."
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={5}
                  focusBorderColor={actionType === "invited" ? "green.400" : "red.400"}
                />

                <Flex justify="flex-end" gap={3} mt={4}>
                  <Button onClick={onClose} variant="outline">
                    Cancel
                  </Button>
                  <Button
                    colorScheme={actionType === "invited" ? "green" : "red"}
                    type="submit"
                    isLoading={isMutating}
                  >
                    {actionType === "invited" ? "Accept" : "Decline"}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>

    </Box>
  );
}
